/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { useState, useEffect } from "react";
import { cn } from "tailwind-variants";
import { getQueryParams, removeQueryParam } from "common/utils/browser";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { type canva } from "common/utils/models/canva.types";
import canvaStore from "./canvaStore";
import CanvaModal from "./CanvaModal.vue";
import CanvaPortal from "./CanvaPortal.vue";
import CanvaTrigger from "./CanvaTrigger.vue";

declare const jQuery: (selector: string | Element | null) => { modal(action: string): void };

type CanvaButtonProps = {
  fileLanding: string;
  namespace: string;
  replica?: boolean;
  settings?: canva.settings.Response | Record<string, never>;
  skipSettingsLoading?: boolean;
  skipInitialModalShow?: boolean;
  onClick?: () => void;
  className?: string;
  id?: string;
};

export default function CanvaModule({
  fileLanding,
  namespace,
  replica = false,
  settings = {} as Record<string, never>,
  skipSettingsLoading = false,
  skipInitialModalShow = false,
  onClick,
  ...restProps
}: CanvaButtonProps) {
  const AUTO_START_QUERY_PARAM = "open_canva_modal_on_start";
  const store = canvaStore.useStore();
  const [modalRender, setModalRender] = useState(false);
  const triggerId = `canva-button-${namespace}`;

  function handleCanva(skipCanvaModal = false) {
    if (onClick) onClick();
    const args = getQueryParams() as Record<string, string | string[]>;
    if (!args[AUTO_START_QUERY_PARAM]) {
      submitDtfEvent("canva_button_click", { namespace });
    }
    const storeSettings = store.getters["canva/settings"] as
      | canva.settings.Response
      | Record<string, never>;
    if (!("authorized" in storeSettings)) return;
    if (!storeSettings.authorized) {
      window.location.href = storeSettings.url;
      return;
    }
    setModalRender(true);
    queueMicrotask(() => {
      if (!skipCanvaModal) {
        jQuery(store.getters["canva/$modal"] as HTMLElement | null).modal("show");
      }
      store.dispatch("canva/resetModalState");
    });
  }

  function handleProxy() {
    document.getElementById(triggerId)?.click();
  }

  const settingsKey = JSON.stringify(settings);
  useEffect(() => {
    store.commit("canva/setSettings", settings);
  }, [settingsKey]);

  useEffect(() => {
    async function mount() {
      if (settings) {
        store.commit("canva/setSettings", settings);
      }
      if (replica) return;
      store.commit("canva/$setFileInput", document.querySelector(fileLanding));
      if (!skipSettingsLoading) {
        await store.dispatch("canva/fetchSettings", namespace);
      }
      // TODO(vue-migration): async callbacks read store getters after await — possible stale closure
      const args = getQueryParams() as Record<string, string | string[]>;
      const storeSettings = store.getters["canva/settings"] as
        | canva.settings.Response
        | Record<string, never>;
      if (
        args[AUTO_START_QUERY_PARAM] &&
        "authorized" in storeSettings &&
        storeSettings.authorized
      ) {
        handleCanva(skipInitialModalShow);
        removeQueryParam(AUTO_START_QUERY_PARAM);
        submitDtfEvent("canva_account_linked", { namespace });
      }
    }
    mount();
  }, []);

  return (
    <>
      {replica ? (
        <CanvaTrigger
          {...restProps}
          className={cn(`canva-trigger--${namespace}`, restProps.className)}
          onClick={handleProxy}
          data-testid="canva-trigger"
        />
      ) : (
        <>
          <CanvaTrigger
            className={cn(`canva-trigger--${namespace}`)}
            id={triggerId}
            onClick={() => handleCanva()}
            data-testid="canva-trigger-1"
          />

          {modalRender ? (
            <CanvaPortal
              component={CanvaModal}
              props={{
                className: cn(`canva-modal--${namespace}`),
                store
              }}
            />
          ) : null}
        </>
      )}
    </>
  );
}
